// src/pages/RMConsumption.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import API from "../../../api";

export default function RMConsumption() {
  const { projectId } = useParams();

  const [rmRows, setRmRows] = useState([]);
  const [rmPartRows, setRmPartRows] = useState([]);
  const [projectYears, setProjectYears] = useState([]);
  const [capUtil, setCapUtil] = useState([]);
  const [termLoanInput, setTermLoanInput] = useState({ startMonth: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Fetch inputs
  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    API.get(`/user/project-inputs/${projectId}`)
      .then(({ data }) => {
        setRmRows(data.rmConsumptionInputs || []);
        setRmPartRows(data.rmConsumptionParticulars || []);
        setProjectYears(data.projectYears || []);
        setCapUtil(data.capacityUtilizationPerYear || []);
        setTermLoanInput(data.termLoanInput || { startMonth: "" });
      })
      .catch((err) => {
        console.error(err);
        setError("Failed to load RM Consumption data.");
      })
      .finally(() => setLoading(false));
  }, [projectId]);

  const format = (n) =>
    typeof n === "number"
      ? n.toLocaleString("en-IN", { maximumFractionDigits: 2 })
      : "";

  // Parse "mm-yyyy"
  const prod = useMemo(() => {
    const s = termLoanInput.startMonth || "";
    const [m, y] = s.split("-").map((t) => parseInt(t, 10));
    return m && y ? { month: m, year: y } : null;
  }, [termLoanInput.startMonth]);

  // Months active in FY
  const getMonthsActive = (fyLabel) => {
    if (!prod) return 12;
    const fyStart = parseInt(fyLabel.split("-")[0], 10);
    if (isNaN(fyStart)) return 12;
    const fyEnd = fyStart + 1;
    const raw = (fyEnd - prod.year) * 12 + (3 - prod.month + 1);
    return Math.max(0, Math.min(raw, 12));
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center w-full">
        <svg
          className="animate-spin h-10 w-10 text-gray-500"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v8H4z"
          />
        </svg>
      </div>
    );
  }
  if (error) {
    return <div className="text-red-600 text-center py-8 w-full">{error}</div>;
  }

  // 1) Compute Installed Capacity (MT)
  const withInstalled = rmRows.map((r) => {
    const days = Number(r.workingDays) || 0;
    const hrs = Number(r.workingHoursPerDay) || 0;
    const out = Number(r.outputPerHour) || 0;
    return { ...r, installed: (days * hrs * out) / 1000 };
  });

  const totalInstalled = withInstalled.reduce((sum, r) => sum + r.installed, 0);

  // 2) Grand production costs
  const grandProduction = projectYears.map((yr, yi) => {
    const util = Number(capUtil[yi]) || 0;
    const months = getMonthsActive(yr);
    return withInstalled.reduce((sumProd, r) => {
      const production = (r.installed * util * months) / (100 * 12);
      const parts = rmPartRows.filter((p) => p.product === r.products);
      return (
        sumProd +
        parts.reduce((s, p) => {
          const qty = Number(p.quantity) || 0;
          const price = Number(p.price) || 0;
          const cons = production * qty;
          const costMT = price * 1000;
          return s + (cons * costMT) / 100000;
        }, 0)
      );
    }, 0);
  });

  return (
    <div className="bg-white">
      {/* 1) RM Details */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4">RM Details</h2>
        <div className="overflow-x-auto shadow-lg rounded-lg">
          <table className="min-w-full table-auto border-collapse">
            <thead className="bg-gray-100">
              <tr className="h-8">
                <th className="px-2 py-1 border border-gray-200 text-left font-medium text-sm">
                  Sl No
                </th>
                <th className="px-2 py-1 border border-gray-200 text-left font-medium text-sm">
                  Product
                </th>
                <th className="px-2 py-1 border border-gray-200 text-right font-medium text-sm">
                  Working Days
                </th>
                <th className="px-2 py-1 border border-gray-200 text-right font-medium text-sm">
                  Hours/Day
                </th>
                <th className="px-2 py-1 border border-gray-200 text-right font-medium text-sm">
                  Output/Hr (KG)
                </th>
                <th className="px-2 py-1 border border-gray-200 text-right font-medium text-sm">
                  1st Yr Period
                </th>
                <th className="px-2 py-1 border border-gray-200 text-right font-medium text-sm">
                  Installed Cap (MT)
                </th>
              </tr>
            </thead>
            <tbody>
              {withInstalled.map((r, i) => (
                <tr key={i} className="even:bg-gray-50">
                  <td className="px-2 py-1 border border-gray-200 text-sm">
                    {i + 1}
                  </td>
                  <td className="px-2 py-1 border border-gray-200 text-sm">
                    {r.products}
                  </td>
                  <td className="px-2 py-1 border border-gray-200 text-sm text-right">
                    {format(Number(r.workingDays))}
                  </td>
                  <td className="px-2 py-1 border border-gray-200 text-sm text-right">
                    {format(Number(r.workingHoursPerDay))}
                  </td>
                  <td className="px-2 py-1 border border-gray-200 text-sm text-right">
                    {format(Number(r.outputPerHour))}
                  </td>
                  <td className="px-2 py-1 border border-gray-200 text-sm text-right">
                    {r.operationPeriod}
                  </td>
                  <td className="px-2 py-1 border border-gray-200 text-sm text-right">
                    {format(r.installed)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-200 font-bold">
                <td
                  colSpan={6}
                  className="px-2 py-1 border border-gray-200 text-sm text-right"
                >
                  Total Installed Cap (MT)
                </td>
                <td className="px-2 py-1 border border-gray-200 text-sm text-right">
                  {format(totalInstalled)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      {/* 2) Yearly Utilization */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4">Yearly Utilization (MT)</h2>
        <div className="overflow-x-auto shadow-lg rounded-lg">
          <table className="min-w-full table-auto border-collapse">
            <thead className="bg-gray-100">
              <tr className="h-8">
                <th className="px-2 py-1 border border-gray-200 text-left font-medium text-sm">
                  Year
                </th>
                <th className="px-2 py-1 border border-gray-200 text-left font-medium text-sm">
                  Utilisation (%)
                </th>
                {withInstalled.map((r, idx) => (
                  <th
                    key={idx}
                    className="px-2 py-1 border border-gray-200 text-right font-medium text-sm"
                  >
                    {r.products}
                  </th>
                ))}
                <th className="px-2 py-1 border border-gray-200 text-right font-medium text-sm">
                  Total (MT)
                </th>
              </tr>
            </thead>
            <tbody>
              {projectYears.map((yr, yi) => {
                const util = Number(capUtil[yi]) || 0;
                const months = getMonthsActive(yr);
                const vals = withInstalled.map(
                  (r) => (r.installed * util * months) / (100 * 12)
                );
                const total = vals.reduce((a, b) => a + b, 0);
                return (
                  <tr key={yi} className="even:bg-gray-50">
                    <td className="px-2 py-1 border border-gray-200 text-sm">
                      {yr}
                    </td>
                    <td className="px-2 py-1 border border-gray-200 text-sm text-right">
                      {format(util)}
                    </td>
                    {vals.map((v, i) => (
                      <td
                        key={i}
                        className="px-2 py-1 border border-gray-200 text-sm text-right"
                      >
                        {format(v)}
                      </td>
                    ))}
                    <td className="px-2 py-1 border border-gray-200 text-sm text-right">
                      {format(total)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* 3) Particulars Totals & Wastage */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4">
          Particulars Totals & Wastage
        </h2>
        <div className="overflow-x-auto shadow-lg rounded-lg">
          <table className="min-w-full table-auto border-collapse">
            <thead className="bg-gray-100">
              <tr className="h-8">
                <th className="px-2 py-1 border border-gray-200 text-left font-medium text-sm">
                  Particular
                </th>
                {withInstalled.map((r, idx) => (
                  <th
                    key={idx}
                    className="px-2 py-1 border border-gray-200 text-right font-medium text-sm"
                  >
                    {r.products}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rmPartRows.map((p, idx) => (
                <tr key={idx} className="even:bg-gray-50">
                  <td className="px-2 py-1 border border-gray-200 text-sm">
                    {p.particular}
                  </td>
                  {withInstalled.map((r, i) => (
                    <td
                      key={i}
                      className="px-2 py-1 border border-gray-200 text-sm text-right"
                    >
                      {p.product === r.products
                        ? format(Number(p.quantity))
                        : ""}
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="bg-gray-200 font-bold">
                <td className="px-2 py-1 border border-gray-200 text-sm">
                  Total
                </td>
                {withInstalled.map((r) => {
                  const sum = rmPartRows
                    .filter((p) => p.product === r.products)
                    .reduce((s, p) => s + (Number(p.quantity) || 0), 0);
                  return (
                    <td
                      key={r.products}
                      className="px-2 py-1 border border-gray-200 text-sm text-right"
                    >
                      {format(sum)}
                    </td>
                  );
                })}
              </tr>
              <tr>
                <td className="px-2 py-1 border border-gray-200 text-sm">
                  Production
                </td>
                {withInstalled.map((_, i) => (
                  <td
                    key={i}
                    className="px-2 py-1 border border-gray-200 text-sm text-right"
                  >
                    1
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-2 py-1 border border-gray-200 text-sm">
                  Wastage
                </td>
                {withInstalled.map((r) => {
                  const sum = rmPartRows
                    .filter((p) => p.product === r.products)
                    .reduce((s, p) => s + (Number(p.quantity) || 0), 0);
                  return (
                    <td
                      key={r.products}
                      className="px-2 py-1 border border-gray-200 text-sm text-right"
                    >
                      {format(sum - 1)}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* 4) Per-Product Detailed Analysis */}
      {withInstalled.map((r, pi) => {
        const productionByYear = projectYears.map((yr, yi) => {
          const util = Number(capUtil[yi]) || 0;
          const months = getMonthsActive(yr);
          return (r.installed * util * months) / (100 * 12);
        });
        const parts = rmPartRows.filter((p) => p.product === r.products);

        return (
          <section key={pi} className="mb-8">
            <h2 className="text-xl font-semibold mb-4">{r.products}</h2>
            <div className="overflow-x-auto shadow-lg rounded-lg">
              <table className="min-w-full table-auto border-collapse">
                <thead className="bg-gray-100">
                  <tr className="h-8">
                    <th className="px-2 py-1 border border-gray-200 text-left font-medium text-sm">
                      Row
                    </th>
                    {projectYears.map((yr, yi) => (
                      <th
                        key={yi}
                        className="px-2 py-1 border border-gray-200 text-right font-medium text-sm"
                      >
                        {yr}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="font-semibold">
                    <td className="px-2 py-1 border border-gray-200 text-sm">
                      Annual Production (MT)
                    </td>
                    {productionByYear.map((val, yi) => (
                      <td
                        key={yi}
                        className="px-2 py-1 border border-gray-200 text-sm text-right"
                      >
                        {format(val)}
                      </td>
                    ))}
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-2 py-1 border border-gray-200 text-sm">
                      Annual Consumption of RM (MT)
                    </td>
                    {projectYears.map((_, yi) => (
                      <td
                        key={yi}
                        className="px-2 py-1 border border-gray-200 text-sm"
                      />
                    ))}
                  </tr>
                  {parts.map((p, xi) => (
                    <tr key={xi} className="even:bg-gray-50">
                      <td className="px-2 py-1 border border-gray-200 text-sm">
                        {p.particular}
                      </td>
                      {productionByYear.map((prodVal, yi) => {
                        const cons = prodVal * (Number(p.quantity) || 0);
                        return (
                          <td
                            key={yi}
                            className="px-2 py-1 border border-gray-200 text-sm text-right"
                          >
                            {format(cons)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  <tr className="bg-gray-100">
                    <td className="px-2 py-1 border border-gray-200 text-sm">
                      Cost of RM per KG (Rs)
                    </td>
                    {projectYears.map((_, yi) => (
                      <td
                        key={yi}
                        className="px-2 py-1 border border-gray-200 text-sm"
                      />
                    ))}
                  </tr>
                  {parts.map((p, xi) => (
                    <tr key={xi}>
                      <td className="px-2 py-1 border border-gray-200 text-sm">
                        {p.particular}
                      </td>
                      {projectYears.map((_, yi) => (
                        <td
                          key={yi}
                          className="px-2 py-1 border border-gray-200 text-sm text-right"
                        >
                          {format(Number(p.price))}
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr className="bg-gray-50">
                    <td className="px-2 py-1 border border-gray-200 text-sm">
                      Cost of RM per MT (Rs)
                    </td>
                    {projectYears.map((_, yi) => (
                      <td
                        key={yi}
                        className="px-2 py-1 border border-gray-200 text-sm"
                      />
                    ))}
                  </tr>
                  {parts.map((p, xi) => (
                    <tr key={xi}>
                      <td className="px-2 py-1 border border-gray-200 text-sm">
                        {p.particular}
                      </td>
                      {projectYears.map((_, yi) => (
                        <td
                          key={yi}
                          className="px-2 py-1 border border-gray-200 text-sm text-right"
                        >
                          {format((Number(p.price) || 0) * 1000)}
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr className="bg-gray-200 font-bold">
                    <td className="px-2 py-1 border border-gray-200 text-sm">
                      Total Cost of RM (Rs in Lakhs)
                    </td>
                    {projectYears.map((_, yi) => {
                      const totalLakhs = parts.reduce((sum, p) => {
                        const cons =
                          productionByYear[yi] * (Number(p.quantity) || 0);
                        const costMT = (Number(p.price) || 0) * 1000;
                        return sum + (cons * costMT) / 100000;
                      }, 0);
                      return (
                        <td
                          key={yi}
                          className="px-2 py-1 border border-gray-200 text-sm text-right"
                        >
                          {format(totalLakhs)}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        );
      })}

      {/* 5) Grand Production */}
      <section>
        <h2 className="text-xl font-semibold mb-4">
          Grand Production (Rs in Lakhs)
        </h2>
        <div className="overflow-x-auto shadow-lg rounded-lg">
          <table className="min-w-full table-auto border-collapse">
            <thead className="bg-gray-100">
              <tr className="h-8">
                <th className="px-2 py-1 border border-gray-200 text-left font-medium text-sm">
                  Grand Production
                </th>
                {projectYears.map((yr, yi) => (
                  <th
                    key={yi}
                    className="px-2 py-1 border border-gray-200 text-right font-medium text-sm"
                  >
                    {yr}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="bg-gray-200 font-bold">
                <td className="px-2 py-1 border border-gray-200 text-sm">
                  Total Cost of RM (Lakhs)
                </td>
                {grandProduction.map((val, yi) => (
                  <td
                    key={yi}
                    className="px-2 py-1 border border-gray-200 text-sm text-right"
                  >
                    {format(val)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
